import * as React from 'react';
import { DetailsList, DetailsListLayoutMode, IColumn, MarqueeSelection, Stack, Selection, ShimmeredDetailsList, ActionButton, Separator, SelectionMode, ProgressIndicator, Link, Icon, useTheme, IDetailsColumnStyles, IDetailsHeaderStyles, IRenderFunction, IDetailsHeaderProps, DetailsHeader, CommandButton } from '@fluentui/react';
import { IUserListItem } from '../../../../Models/Admin/IUserListItem';
import { PageTitle } from '../../../PageTitle';
import { UserListFilter } from './UserListFilter';


interface IItemOrder {
    columnKey: string | null;
    isAsc: boolean;
}

interface IUserListViewProps {
    items: IUserListItem[];
    onUserSelected: (id?: string) => void;
    editUser: (id: string) => void;
    onNewUser: () => void;
    removeUser: () => void;
    isLoading: boolean;   
    selectedUser?: string;
    text: string;
    setText: (text: string) => void;
    role: string;
    setRole: (text: string) => void;
    aktivni: boolean;
    setAktivni: (aktivni: boolean) => void;
    exportToCsv: () => void;
    order: IItemOrder;
    setOrder: (order: IItemOrder) => void;
}

export const UserListView: React.FunctionComponent<IUserListViewProps> = (props) => {

    const [selected, setSelected] = React.useState<IUserListItem[]>([]);
    const [selectionState] = React.useState(new Selection({

        onSelectionChanged: () => {
            const selected = selectionState.getSelection() as IUserListItem[];
            setSelected(selected);
        },
    }));

    React.useEffect(() => {
        if (selected.length > 0) {
            props.onUserSelected(selected[0].id);
        }
        else {
            props.onUserSelected(undefined);
        }
    }, [selected])

    const theme = useTheme();

    const headerStyle: Partial<IDetailsColumnStyles> = {
        cellName: {
            fontFamily: "Comfortaa, serif",
            color: theme.palette.blue,
        },
    }

    const columnClickCallback = (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        if (!column.isSorted) {
            props.setOrder({ columnKey: column.key, isAsc: true });
        } else {
            if (!column.isSortedDescending) {
                props.setOrder({ columnKey: column.key, isAsc: false });
            } else {
                props.setOrder({ columnKey: null, isAsc: true });
            }
        }
    }

    const [columns, setColumns] = React.useState<IColumn[]>(
        [
            { key: 'lastName', name: 'Příjmení', fieldName: 'lastName', minWidth: 100, maxWidth: 200, isResizable: false, styles: headerStyle, onColumnClick: columnClickCallback },
            { key: 'firstName', name: 'Jméno', fieldName: 'firstName', minWidth: 100, maxWidth: 200, isResizable: false, styles: headerStyle, onColumnClick: columnClickCallback },
            { key: 'email', name: 'Email', isCollapsible: true, fieldName: 'email', minWidth: 100, maxWidth: 200, isResizable: false, styles: headerStyle, onColumnClick: columnClickCallback  },
            { key: 'company', name: 'Firma', isCollapsible: true, fieldName: 'company', minWidth: 100, maxWidth: 200, isResizable: false, styles: headerStyle, onColumnClick: columnClickCallback  },
            { key: 'street', name: 'Ulice', isCollapsible: true, fieldName: 'street', minWidth: 100, maxWidth: 200, isResizable: false, styles: headerStyle, onColumnClick: columnClickCallback  },
            { key: 'city', name: 'Město', isCollapsible: true, fieldName: 'city', minWidth: 100, maxWidth: 200, isResizable: false, styles: headerStyle, onColumnClick: columnClickCallback  },
            { key: 'deleteButton', name: "delete", minWidth: 50, isIconOnly: true, styles: headerStyle  }
        ]
    );

    const headerRowStyle: Partial<IDetailsHeaderStyles> = {
        root: {
            borderColor: theme.palette.blue,
            borderBottomWidth: 2
        }
    }

    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (props, defaultRender) => {
        if (props) {
            props = { ...props, styles: headerRowStyle };
            return (
                <DetailsHeader
                    {...props}
                />
            );
        }
        return null;
    }

    const renderItemColumn = (item: IUserListItem, index?: number, column?: IColumn) => {
        if (index != undefined && column != undefined) {
            const fieldContent = item[column.fieldName as keyof IUserListItem];
            switch (column.key) {
                case "lastName":
                    return <span style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                        <Link onClick={() => props.editUser(item.id)}>{fieldContent}</Link>
                    </span>
                 default:
                    return <span style={{ height: '100%', display: 'flex', alignItems: 'center' }}>{fieldContent}</span>;
            }
        }
        else {
            return (<div></div>)
        }
    }

    React.useEffect(() => {
        if (props.order) {
            let newColumns = columns.map(x => {
                if (x.key === props.order.columnKey) {
                    return { ...x, isSorted: true, isSortedDescending: !props.order.isAsc }
                }
                else {
                    return { ...x, isSorted: false, isSortedDescending: false }
                }
            })
            setColumns(newColumns);
        }
    }, [props.order])


    return (
        <Stack styles={{ root: { paddingLeft: 20, paddingRight:20 } }}>
            <PageTitle title="Aktivní uživatelé" />
            <UserListFilter text={props.text} setText={props.setText}
                role={props.role} setRole={props.setRole}
                aktivni={props.aktivni} setAktivni={props.setAktivni}
            />
            <Stack horizontal tokens={{ childrenGap:10 }}>
            <ActionButton iconProps={{ iconName: "AddFriend" }} onClick={props.onNewUser}>Přidat uživatele</ActionButton>
                <ActionButton disabled={props.selectedUser === undefined} iconProps={{ iconName: "Delete" }} onClick={props.removeUser}>Odstranit uživatele</ActionButton>
                <ActionButton onClick={props.exportToCsv} iconProps={{ iconName: "Save" }} text="Exportovat do CSV" />

            </Stack>
            <Stack styles={{ root: { height:10 } }}>
                {props.isLoading ? <ProgressIndicator /> : null}
            </Stack>
                 <DetailsList
                items={props.items}
                columns={columns}
                setKey="set"
                onRenderItemColumn={renderItemColumn}
                selectionMode={SelectionMode.single}
                layoutMode={DetailsListLayoutMode.justified}
                selection={selectionState}
                onRenderDetailsHeader={onRenderDetailsHeader}
                selectionPreservedOnEmptyClick={true}
                ariaLabelForSelectionColumn="Výběr uživatele"
                ariaLabelForSelectAllCheckbox="Vyber všechny uživatele"
                checkButtonAriaLabel="Vyber řádek"
                onItemInvoked={(item: IUserListItem) => props.editUser(item.id)}
               
                />
                
             
        </Stack>
    )
}